import { map, tap } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject, Subject,  AsyncSubject, of, from } from "rxjs";
import * as AzureStorage from '../../../assets/azure/azure-storage.blob.min.js';
import { RequestForScanning } from "../models/scan-documents.model";
import { DomainValue, TagDefinition } from "../models/document-tag.model";
import { ChiefsOrderService } from '../../services/chiefsOrder.service';
import { AuthService } from '../../services/auth.service';
import { BlobDownloadRequest, CommitProvisionedFileWithKeywordsRequest, OnBaseDocumentDetails, QueryResult, OnBaseQueryModel, OnBaseDocumentGroup, OnBaseDocumentType, OnBaseKeyword, OnBaseKeywordMappingSearchSpec, OnBaseKeywordSimple, OnBaseDocumentTypeSearchSpec, OnBaseMappedKeyword, OnBaseProgramArea, OnBaseMappedDocumentGroup, OnBaseUploadedFileLogSearchSpec, OnBaseFileUploadLog, OnBaseKeywordTypeSearchSpec, FileQueueEntry, FileQueueQuery } from '../models/onBaseIntegrationAndMapping.model';
import { ParserPropertyModel, SampleFileRequest, ParseFileRequest, BulkWellStatusChangeParseRequest, ParseFileResponse, ValidateAPIRequest, BulkNAD83UpdateParseRequest } from "../../builder/components/state/controls/config/data/file-parser-config/parser.model";

export interface ITag {
        id: string;
        value: any;
}

export interface IDocument {
    id?: string;
    documentType: string;
    contentType: string;
    name: string;
    auditUser: string;
    auditDate: Date;
    tags?: ITag[]
}

export interface IUploadFile {
    rawFile: File | null;
    completed: boolean;
    canBeDeleted?: boolean;
    sentToOnbase?: boolean;
    error?: string | boolean;
    isUploading: boolean;
    waiting?: boolean;
    percentage: number;
    path: string;
    relativePath?: string;
    fileName: string;
    type: string;
    uploadedByUserId?: string;
    uploadedByOrganizationId?: string;
    blobUri?: string;
    documentType?: string;
    id?: number;
}

export interface IAzureBlobSasToken {
  fileName: string;
  storageUri: string;
  blobUri: string;
  sasToken: string;
  container: string;
}

export interface UploadedFile extends IUploadFile{
    organizationId: string;
    organizationName: string;
}

@Injectable()
export class DocumentManagementService {

    public readonly INVALID_EXTENSION: string = "Invalid file extension";
    public readonly UNKNOWN_FILE: string = "Unknown file extension";
    public readonly NO_FILE_NAME: string = "No file name provided";
    public readonly WHITELIST_ERRORS = [ this.INVALID_EXTENSION, this.UNKNOWN_FILE, this.NO_FILE_NAME ];
    public static readonly DocumentTypeId: string = "0000000000000000";
 
  _ensureData(): void {
    if (this._tagTypes.value.length === 0) {
      this.http.get<TagDefinition[]>(`/api/docs/tag-types`).subscribe(tags => {
        if (this._tagTypes.value.length === 0) {
          let documentTypes = tags.filter(t => t.id === DocumentManagementService.DocumentTypeId)[0].domainValues
            .sort((a, b) => (a.displayName || "").localeCompare(b.displayName));
          let tagTypes = tags.filter(t => t.id !== DocumentManagementService.DocumentTypeId)
            .sort((a, b) => (a.name || "").localeCompare(b.name));
          this._tagTypes.next(tagTypes);
          this._documentTypes.next(documentTypes);
        }
      });
    }
    if (this._whiteList.value.length === 0) {
      this.http.get<string[]>(`/api/docs/whitelist`).subscribe(list => {
        this._whiteList.next(list);
      });
    }
  }

  public _tagTypes: BehaviorSubject<TagDefinition[]> = new BehaviorSubject<TagDefinition[]>([]);
  public _documentTypes: BehaviorSubject<DomainValue[]> = new BehaviorSubject<DomainValue[]>([]);
  public _whiteList: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);   
    
  get documentTypes(): Observable<DomainValue[]> {
    this._ensureData();
    return this._documentTypes;
  }
  get tagTypes(): Observable<TagDefinition[]> {
    this._ensureData();
    return this._tagTypes;
  }

  get whiteList(): Observable<string[]> {
    this._ensureData();
    return this._whiteList;
  }

  constructor(
      public http: HttpClient, public coSrv: ChiefsOrderService, public authSrv: AuthService  ) { }

  public downloadFile(provisionTarget: string): Observable<string> {
    return this.http.get<IAzureBlobSasToken>("api/docs/get-download-link", { params: { blobUri: provisionTarget } }).pipe(map(t => {
      return t.blobUri + t.sasToken;
    }));
  }

  public downloadStoredDocument(documentTypeId: string, docId: string): string {
    const url = window.location.href.substring(0, (window.location.href.indexOf(window.location.pathname)));
    return `${url}/api/docs/download-borked/${documentTypeId}/${docId}`;
    }

    getStoredDocument(documentTypeId: string, docId: string): Observable<any> {
        return this.http.get(this.downloadStoredDocument(documentTypeId, docId), { responseType: 'blob' as 'blob' });
    }

  public getProvisionedAttachments(): Observable<IAzureBlobSasToken[]> {
    return this.http.get<IAzureBlobSasToken[]>(`/api/docs/get-provisioned-attachments`);
  }

  public provision(file: string): Observable<IAzureBlobSasToken> {
    return this.http.post<IAzureBlobSasToken>(`/api/docs/provision`, { 'file': file });
  }

  public provisionAttachment(file: string): Observable<IAzureBlobSasToken> {
    return this.http.post<IAzureBlobSasToken>(`/api/docs/provisionAttachment`, { 'file': file });
  }

  /**
   * Returns true if file delete was successful
   * @param file
   */
  public abandon(file: IUploadFile): Observable<any> {
    return this.http.post('/api/docs/abandon', { file: file.blobUri });
  }

    public processProvisionedFile(file: IUploadFile, token: IAzureBlobSasToken, uploadComplete: Subject<IAzureBlobSasToken>): void {
    let blobService = AzureStorage.createBlobServiceWithSas(token.storageUri, token.sasToken).withFilter(new AzureStorage.ExponentialRetryPolicyFilter());
    let blockSize = file.rawFile.size > 1024 * 1024 * 32 ? 1024 * 1024 * 4 : 1024 * 512;
    let options = {
      storeBlobContentMD5: false,
      blockSize: blockSize
    };

    blobService.singleBlobPutThresholdInBytes = blockSize;
    file.waiting = false;
    file.error = null;
    file.isUploading = true;
    file.percentage = 0;
    file.blobUri = token.blobUri;

    let updater = blobService.createBlockBlobFromBrowserFile(token.container, token.fileName, file.rawFile, options, (error, result, response) => {
      if (error) {
        file.completed = false;
        file.error = error;
        uploadComplete.error(error);
        uploadComplete.complete();
      }
      else {
        file.path = `${token.storageUri}${token.container}/${token.fileName}`;
        file.fileName = file.rawFile.name;
      }
    });

    updater.on('progress', function () {
      file.percentage = updater.getCompletePercent();
      if (file.percentage >= 100) {
        file.isUploading = false;
        file.completed = true;
        uploadComplete.next(token);
        uploadComplete.complete();
      }
    });
    }

    public saveProvisionedFile(file: IUploadFile, token: IAzureBlobSasToken, uploadComplete: Subject<IAzureBlobSasToken>, coInstanceId: string = null, isSupplemental: boolean = false): AsyncSubject<boolean> {
        let success = new AsyncSubject<boolean>();
        let blobService = AzureStorage.createBlobServiceWithSas(token.storageUri, token.sasToken).withFilter(new AzureStorage.ExponentialRetryPolicyFilter());
        let blockSize = file.rawFile.size > 1024 * 1024 * 32 ? 1024 * 1024 * 4 : 1024 * 512;
        let options = {
            storeBlobContentMD5: false,
            blockSize: blockSize
        };

        blobService.singleBlobPutThresholdInBytes = blockSize;
        file.waiting = false;
        file.error = null;
        file.isUploading = true;
        file.percentage = 0;
        file.blobUri = token.blobUri;

        let callBack = (error, result, response) => {
            if (error) {
                file.completed = false;
                file.error = error;
                uploadComplete.error(error);
                uploadComplete.complete();
                success.next(false);
                success.complete();
            }
            else {
                file.path = `${token.storageUri}${token.container}/${token.fileName}`;
                file.fileName = file.rawFile.name;
                file.blobUri = token.blobUri;
                file.path = token.blobUri;
                file.percentage = 100;
                file.isUploading = false;
                file.waiting = false;
                file.completed = true;
                file.uploadedByOrganizationId = this.authSrv.User.organizationId;
                if (coInstanceId != null) {
                    this.coSrv.saveFilesToContext(file, coInstanceId, isSupplemental).subscribe(res => {
                        success.next(true);
                        success.complete();
                    });
                } else {
                    success.next(true);
                    success.complete();
                }                           
            }
        };

        let updater = blobService.createBlockBlobFromBrowserFile(token.container, token.fileName, file.rawFile, options, callBack);
        
        updater.on('progress', function () {
            file.percentage = updater.getCompletePercent();
            if (file.percentage >= 100) {
                file.isUploading = false;
                file.completed = true;                    
                uploadComplete.next(token);
                uploadComplete.complete();               
            }
                                
        });

        return success;    
    }

    generateScanningCover(request: RequestForScanning): Observable<any> {
        const opt = {
            headers: new HttpHeaders({ "Content-Type": "application/json", "Accept": "application/pdf" }),
            responseType: 'blob' as 'json',
            observe: 'response' as'body'
        };
        return this.http.post(`/api/docs/get-scanning-cover`, request, opt);
    }

    downloadBlob(request: BlobDownloadRequest): Observable<any> {
        const opt = {
            headers: new HttpHeaders({ "Content-Type": "application/json", "Accept": request.contentType }),
            responseType: 'blob' as 'blob'
        };
        return this.http.post("/api/docs/download-from-storage", request, opt);
    }

    getAllDocumentGroups(): Observable<OnBaseDocumentGroup[]> {
        return this.http.get<OnBaseDocumentGroup[]>("/api/docs/get-document-groups");
    }

    getAllDocumentTypes(): Observable<OnBaseDocumentType[]> {
        return this.http.get<OnBaseDocumentType[]>("/api/docs/get-document-types");
    }

    findDocumentTypes(spec: OnBaseDocumentTypeSearchSpec): Observable<OnBaseDocumentType[]> {
        return this.http.post<OnBaseDocumentType[]>(`/api/docs/document-type-search`, spec);
    }

    getDocumentTypesByDocumentGroup(groupId: number): Observable<OnBaseDocumentType[]> {
        return this.http.get<OnBaseDocumentType[]>(`/api/docs/get-types-by-group/${groupId}`);
    }

    getDocumentTypesByDocumentGroupName(groupName: string): Observable<OnBaseDocumentType[]> {
        return this.findDocumentTypes({ documentGroupName: groupName } as OnBaseDocumentTypeSearchSpec);
    }

    getKeywordTypesByDocumentType(documentTypeId: number): Observable<OnBaseKeyword[]> {
        return this.http.get<OnBaseKeyword[]>(`/api/docs/get-key-words-by-doc-type/${documentTypeId}`);
    }

    getKeywordTypesByDocumentTypeName(documentTypeName: string): Observable<OnBaseKeyword[]> {
        return this.findKeywordTypes({ documentTypeName: documentTypeName } as OnBaseKeywordTypeSearchSpec);;
    }

    getAllKeywords(): Observable<OnBaseKeyword[]> {
        return this.http.get<OnBaseKeyword[]>("/api/docs/get-key-words");
    }

    findKeywordTypes(spec: OnBaseKeywordTypeSearchSpec): Observable<OnBaseKeyword[]> {
        return this.http.post<OnBaseKeyword[]>(`/api/docs/keyword-type-search`, spec);
    }

    findKeywordMappings(searchSpec: OnBaseKeywordMappingSearchSpec): Observable<OnBaseMappedKeyword[]> {
        const url = `/api/onBaseIntegrationAndMapping/find-mapped-key-words`;
        let mappedKeywords = this.http.post<Array<OnBaseMappedKeyword>>(url, searchSpec);
        return mappedKeywords;
    }

    saveKeyword(mappedKeyword: OnBaseKeywordSimple): Observable<number> {
        const url = `/api/onBaseIntegrationAndMapping/keywords/save`;
        let id = this.http.post<number>(url, mappedKeyword);
        return id;
    }

    saveKeywordMapping(mappedKeyword: OnBaseMappedKeyword): Observable<boolean> {
        const url = `/api/onBaseIntegrationAndMapping/keywordMapping/save`;
        let success = this.http.post<boolean>(url, mappedKeyword);
        return success;
    }

    removeKeywordMapping(keywordName: string, documentTypeName: string, documentGroupName: string): Observable<boolean> {
        const url = `/api/onBaseIntegrationAndMapping/keywordMapping/remove/${keywordName}/${documentTypeName}/${documentGroupName}`;
        let success = this.http.delete<boolean>(url);
        return success;
    }

    deleteProgramArea(programAreaId: number): Observable<boolean> {
        const url = `/api/onBaseIntegrationAndMapping/programArea/${programAreaId}/`;
        let success = this.http.delete<boolean>(url);
        return success;
    }

    getAllProgramAreas(): Observable<OnBaseProgramArea[]> {
        const url = `/api/onBaseIntegrationAndMapping/programAreas/all`;
        let programAreas = this.http.get<OnBaseProgramArea[]>(url);
        return programAreas;
    }

    getAllMappedDocumentGroups(): Observable<OnBaseMappedDocumentGroup[]> {
        const url = `/api/onBaseIntegrationAndMapping/documentGroups/all`;
        let programAreas = this.http.get<OnBaseMappedDocumentGroup[]>(url);
        return programAreas;
    }

    saveProgramArea(programArea: OnBaseProgramArea): Observable<number> {
        const url = `/api/onBaseIntegrationAndMapping/programAreas/save/`;
        let programAreaId = this.http.post<number>(url, programArea);
        return programAreaId;
    }

    saveDocumentGroup(documentGroup: OnBaseDocumentGroup): Observable<boolean> {
        const url = `/api/onBaseIntegrationAndMapping/documentGroups/save/`;
        let sucess = this.http.post<boolean>(url, documentGroup);
        return sucess;
    }

    commitScanRequest(workOrderId: string, request: RequestForScanning): Observable<number[]> {
        return this.http.post<number[]>(`/api/docs/commit-scan-request/${workOrderId}`, request);
    }

    commitProvisionedFilesToOnBase(request: CommitProvisionedFileWithKeywordsRequest[]): Observable<string[]> {
        return this.http.post<string[]>(`/api/docs/commit-provisioned-files`, request);
    }

	query(model: OnBaseQueryModel): Observable<QueryResult> {
        return this.http.post<QueryResult>(`/api/docs/query`, model);
    }

    download(documentId: number): Observable<HttpResponse<Blob>> {
        return this.http.get<Blob>(`/api/docs/download/${documentId}`, { observe: 'response', responseType: 'blob' as 'json' });
    }

    downloadFromQueue(documentId: string | number): Observable<HttpResponse<Blob>> {      
        return this.http.get<Blob>(`/api/docs/download-from-queue/${documentId}`, { observe: 'response', responseType: 'blob' as 'json' });
    }

    getDocumentDetails(documentId: number): Observable<OnBaseDocumentDetails> {
        return this.http.get<OnBaseDocumentDetails>(`/api/docs/getDetails/${documentId}`);
    }

    queryQueue(query: FileQueueQuery): Observable<FileQueueEntry[]> {
        return this.http.post<FileQueueEntry[]>("/api/docs/query-queue", query);
    }

    queryQueueByTypeAndName(query: FileQueueQuery): Observable<FileQueueEntry[]> {
        return this.http.post<FileQueueEntry[]>("/api/docs/query-queue-by-type-and-name", query);
    }

    enqueueAll(items: string[]): Observable<boolean> {
        return this.http.post<boolean>("/api/docs/enqueue-all", items);
    }

    enqueueItem(item: string): Observable<boolean> {
        return this.http.get<boolean>(`/api/docs/enqueue/${item}`);
    }

    GetUploadedFileLogs(spec: OnBaseUploadedFileLogSearchSpec): Observable<OnBaseFileUploadLog[]> {
        return this.http.post<OnBaseFileUploadLog[]>(`/api/docs/upload-log-query`, spec);
    }

    generateSampleParserFile(type: string, model: ParserPropertyModel[]): Observable<any> {
        const req = {
            fileType: type,
            model: model
        } as SampleFileRequest;
        const opt = {
            headers: new HttpHeaders({ "Content-Type": "application/json", "Accept": ["text/csv", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"] }),
            responseType: 'blob' as 'blob'
        };
        return this.http.post(`/api/docs/get-parser-sample`, req, opt);
	}

	getSampleBulkNAD83UpdateFile(): Observable<any> {
		const opt = {
			headers: new HttpHeaders({ "Content-Type": "application/json", "Accept": ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"] }),
			responseType: 'blob' as 'blob'
		}
		return this.http.post('/api/docs/get-bulk-nad83-sample', "", opt);
	}

    getSampleBulkWellStatusChangeFile(): Observable<any> {
        const opt = {
            headers: new HttpHeaders({ "Content-Type": "application/json", "Accept": ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"] }),
            responseType: 'blob' as 'blob'
        }
        return this.http.post('/api/docs/get-bulk-well-status-sample', "", opt);
	}

	parseBulkNAD83UpdateRequest(file: IUploadFile): Observable<ParseFileResponse> {
		const request = {
			file: file
		} as BulkNAD83UpdateParseRequest;
		return this.http.post<ParseFileResponse>('api/docs/parse-bulk-nad83-request', request);
	}

    parseBulkWellStatusChangeRequest(file: IUploadFile): Observable<ParseFileResponse> {
        const request = {
            file: file
        } as BulkWellStatusChangeParseRequest;
        return this.http.post<ParseFileResponse>('/api/docs/parse-bulk-well-status-change', request);
    }

    parseFile(file: IUploadFile, model: ParserPropertyModel[], hasHeaders: boolean): Observable<ParseFileResponse> {
        const request = {
            file: file,
			format: model,
			fileHasHeaders: hasHeaders
        } as ParseFileRequest;
        return this.http.post<ParseFileResponse>(`/api/docs/parse-file`, request);
    }

    validateAPIs(organizationId: string, file: IUploadFile, fileHasHeaders: boolean): Observable<ParseFileResponse> {
        const request: ValidateAPIRequest = {
            file: file,
			organizationId: organizationId,
			fileHasHeaders: fileHasHeaders
        } as ValidateAPIRequest
        return this.http.post<ParseFileResponse>('/api/docs/validate-apis', request);
    }


}
